import firebase from "firebase/compat/app";
import "firebase/compat/database";
import "firebase/compat/firestore";
import db, { createFirebaseRef } from "src/db";
import { onConnectionChanged } from "src/db/connection";

const ONLINE_WRITE_TTL_MS = 60 * 1000;

type PresenceConnection = {
  key: string;
  uid: string;
  sessionId: string;
  refCount: number;
  unsubscribe: () => void;
};

let activeConnection: PresenceConnection | null = null;
const lastOnlineWriteByKey = new Map<string, number>();

const buildPresenceKey = (uid: string, sessionId: string) =>
  `${uid}:${sessionId}`;

const databasePresenceState = (state: "online" | "offline", sessionId: string) => ({
  state,
  last_changed: firebase.database.ServerValue.TIMESTAMP,
  session_id: sessionId,
});

const firestoreOnlineState = (sessionId: string) => ({
  state: "online",
  last_changed: firebase.firestore.FieldValue.serverTimestamp(),
  session_id: sessionId,
});

const shouldWriteOnlineState = (key: string, now: number) => {
  const lastWriteAt = lastOnlineWriteByKey.get(key);
  return !lastWriteAt || now - lastWriteAt >= ONLINE_WRITE_TTL_MS;
};

const stopActiveConnection = () => {
  if (!activeConnection) return;

  activeConnection.unsubscribe();
  activeConnection = null;
};

export const startUserPresence = (uid: string, sessionId: string): (() => void) => {
  if (!uid || !sessionId) return () => {};

  const key = buildPresenceKey(uid, sessionId);

  if (activeConnection?.key === key) {
    activeConnection.refCount += 1;
    return () => stopUserPresence(key);
  }

  stopActiveConnection();

  const userStatusDatabaseRef = createFirebaseRef("status", uid);
  const userProfileRef = db.collection("users").doc(uid);

  const unsubscribe = onConnectionChanged((isConnected) => {
    if (!isConnected) {
      userStatusDatabaseRef.set(databasePresenceState("offline", sessionId));
      return;
    }

    userStatusDatabaseRef
      .onDisconnect()
      .set(databasePresenceState("offline", sessionId))
      .then(() => {
        userStatusDatabaseRef.set(databasePresenceState("online", sessionId));

        const now = Date.now();
        if (!shouldWriteOnlineState(key, now)) return;

        lastOnlineWriteByKey.set(key, now);
        userProfileRef.update(firestoreOnlineState(sessionId));
      });
  });

  activeConnection = {
    key,
    uid,
    sessionId,
    refCount: 1,
    unsubscribe,
  };

  return () => stopUserPresence(key);
};

export const stopUserPresence = (key?: string, force = false) => {
  if (!activeConnection) return;
  if (key && activeConnection.key !== key) return;

  if (force) {
    stopActiveConnection();
    return;
  }

  activeConnection.refCount -= 1;
  if (activeConnection.refCount > 0) return;

  stopActiveConnection();
};

export const setUserOffline = (uid: string, sessionId: string) => {
  if (!uid || !sessionId) return Promise.resolve();

  const key = buildPresenceKey(uid, sessionId);
  stopUserPresence(key, true);

  const userStatusDatabaseRef = createFirebaseRef("status", uid);
  return userStatusDatabaseRef.set(databasePresenceState("offline", sessionId));
};

export const __resetUserPresenceForTest = () => {
  stopActiveConnection();
  lastOnlineWriteByKey.clear();
};
